class GamanzaTournamentOrderInterceptor {
  constructor() {
    this.originalFetch = null;
    this.interceptedFetch = null;
    this.gamanzaOrigin = '';
    this.tournamentsByGameSet = new Map();
    this.catalogsByGameSet = new Map();
  }

  gameSetKey(gameIds = []) {
    return [...gameIds].sort().join(',');
  }

  isRubyPlay(game) {
    return game?.providerId?.providerId?.toLowerCase() === 'rubyplay' ||
      game?.providerId?.name === 'RP';
  }

  isAmusnet(game) {
    return game?.providerId?.providerId?.toLowerCase() === 'amusnet' ||
      game?.providerId?.name === 'AN';
  }

  registerTournaments(payload) {
    this.tournamentsByGameSet.clear();
    this.catalogsByGameSet.clear();

    (payload?.tournaments || []).forEach((tournament) => {
      if (!Array.isArray(tournament.games) || !tournament.games.length) {
        return;
      }

      this.tournamentsByGameSet.set(
        this.gameSetKey(tournament.games),
        tournament._id
      );
    });
  }

  sortGamesByProviderPriority(games) {
    return games
      .map((game, index) => ({ game, index }))
      .sort((a, b) => {
        // Temporary test order: RubyPlay, then Amusnet, then all other providers.
        const priorityA = this.isRubyPlay(a.game) ? 0 : this.isAmusnet(a.game) ? 1 : 2;
        const priorityB = this.isRubyPlay(b.game) ? 0 : this.isAmusnet(b.game) ? 1 : 2;

        return priorityA - priorityB || a.index - b.index;
      })
      .map(({ game }) => game);
  }

  async getTournamentCatalog(gameSetKey, url, request) {
    let catalogPromise = this.catalogsByGameSet.get(gameSetKey);

    if (!catalogPromise) {
      const allGamesUrl = new URL(url);
      allGamesUrl.searchParams.set('page', '1');
      allGamesUrl.searchParams.set('limit', '99999');

      catalogPromise = this.originalFetch(allGamesUrl.toString(), {
        method: request.method,
        headers: request.headers,
        credentials: request.credentials,
        mode: request.mode,
        redirect: request.redirect,
        referrer: request.referrer,
        referrerPolicy: request.referrerPolicy
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Gamanza tournament catalog request failed: ${response.status}`);
          }

          const payload = await response.json();
          return {
            payload,
            games: this.sortGamesByProviderPriority(payload.docs || [])
          };
        })
        .catch((error) => {
          this.catalogsByGameSet.delete(gameSetKey);
          throw error;
        });

      this.catalogsByGameSet.set(gameSetKey, catalogPromise);
    }

    return catalogPromise;
  }

  createPaginatedResponse(payload, games, page, limit) {
    const totalDocs = games.length;
    const totalPages = Math.max(1, Math.ceil(totalDocs / limit));
    const start = (page - 1) * limit;
    const paginatedPayload = {
      ...payload,
      docs: games.slice(start, start + limit),
      totalDocs,
      limit,
      totalPages,
      page,
      pagingCounter: start + 1,
      hasPrevPage: page > 1,
      hasNextPage: page < totalPages,
      prevPage: page > 1 ? page - 1 : null,
      nextPage: page < totalPages ? page + 1 : null
    };

    console.log('[Gamanza tournament catalog] returning reordered response', paginatedPayload);

    return new Response(JSON.stringify(paginatedPayload), {
      status: 200,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  install(serviceUrl) {
    if (this.originalFetch || !serviceUrl || typeof window === 'undefined') {
      return;
    }

    this.gamanzaOrigin = new URL(serviceUrl).origin;
    this.originalFetch = window.fetch.bind(window);

    this.interceptedFetch = async (input, init) => {
      const request = new Request(input, init);
      const url = new URL(request.url);

      if (url.origin !== this.gamanzaOrigin) {
        return this.originalFetch(input, init);
      }

      const isTournamentsRequest =
        url.pathname === '/api/tournaments/v1/ui/filter-eligible-tournaments-for-player';

      if (isTournamentsRequest) {
        const response = await this.originalFetch(input, init);

        try {
          this.registerTournaments(await response.clone().json());
        } catch {
          // Leave the tournament map unchanged when Gamanza returns no JSON.
        }

        return response;
      }

      const isTournamentGamesRequest =
        url.pathname === '/game-catalog/v1/ui/games' &&
        url.searchParams.has('gameId');

      if (!isTournamentGamesRequest) {
        return this.originalFetch(input, init);
      }

      const gameIds = url.searchParams.get('gameId')
        .split(',')
        .filter(Boolean);
      const gameSetKey = this.gameSetKey(gameIds);
      const tournamentId = this.tournamentsByGameSet.get(gameSetKey);

      if (!tournamentId) {
        return this.originalFetch(input, init);
      }

      try {
        const catalog = await this.getTournamentCatalog(gameSetKey, url, request);
        const page = Number(url.searchParams.get('page') || 1);
        const limit = Number(url.searchParams.get('limit') || 16);

        return this.createPaginatedResponse(catalog.payload, catalog.games, page, limit);
      } catch (error) {
        console.warn('Unable to reorder Gamanza tournament catalog:', error);
        return this.originalFetch(input, init);
      }
    };

    window.fetch = this.interceptedFetch;
  }

  destroy() {
    if (typeof window !== 'undefined' && window.fetch === this.interceptedFetch && this.originalFetch) {
      window.fetch = this.originalFetch;
    }

    this.originalFetch = null;
    this.interceptedFetch = null;
    this.gamanzaOrigin = '';
    this.tournamentsByGameSet.clear();
    this.catalogsByGameSet.clear();
  }
}

export default new GamanzaTournamentOrderInterceptor();
